import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, Check, AlertCircle, ScanText, ExternalLink } from "lucide-react";
import { toast } from "sonner";

// Human-friendly labels per doc type
const FIELD_LABELS = {
  rc: [
    ["registration_number", "Registration #"],
    ["owner_name", "Owner"],
    ["father_name", "Father"],
    ["vehicle_class", "Class"],
    ["maker", "Maker"],
    ["model", "Model"],
    ["chassis_number", "Chassis #"],
    ["engine_number", "Engine #"],
    ["fuel_type", "Fuel"],
    ["color", "Color"],
    ["registration_date", "Registered on"],
    ["insurance_valid_upto", "Insurance valid till"],
  ],
  driving_license: [
    ["dl_number", "DL #"],
    ["name", "Name"],
    ["father_name", "Father"],
    ["date_of_birth", "DOB"],
    ["issue_date", "Issued"],
    ["valid_till", "Valid till"],
    ["blood_group", "Blood group"],
    ["issuing_authority", "Authority"],
  ],
  policy: [
    ["insurer_name", "Insurer"],
    ["policy_number", "Policy #"],
    ["policy_holder", "Policy holder"],
    ["vehicle_registration_number", "Vehicle reg #"],
    ["vehicle_make", "Make"],
    ["vehicle_model", "Model"],
    ["policy_type", "Type"],
    ["policy_start_date", "Start"],
    ["policy_end_date", "End"],
    ["sum_insured_idv", "IDV (₹)"],
    ["total_premium", "Premium (₹)"],
    ["ncb_percentage", "NCB %"],
  ],
};

function formatValue(k, v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number" && (k.includes("premium") || k.includes("idv"))) {
    return `₹${v.toLocaleString("en-IN")}`;
  }
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

export default function DocumentOCR({ docId, docType, category, lastExtraction }) {
  const [extracted, setExtracted] = useState(lastExtraction || null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(Boolean(lastExtraction));
  const [insurers, setInsurers] = useState([]);

  // Cache the insurer list once so we can fuzzy-match names extracted by Gemini
  useEffect(() => {
    if (docType === "policy" && insurers.length === 0) {
      api.get("/insurers").then((r) => setInsurers(r.data)).catch(() => {});
    }
  }, [docType, insurers.length]);

  const suggestedInsurer = (() => {
    if (docType !== "policy" || !extracted?.insurer_name || insurers.length === 0) return null;
    const q = String(extracted.insurer_name).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    if (!q) return null;
    // Score by number of matching word tokens
    let best = null; let bestScore = 0;
    for (const ins of insurers) {
      const tokens = ins.name.toLowerCase().replace(/[^a-z0-9]+/g, " ").split(/\s+/).filter(Boolean);
      const score = tokens.reduce((acc, t) => acc + (q.includes(t) ? t.length : 0), 0);
      if (score > bestScore) { bestScore = score; best = ins; }
    }
    return bestScore >= 3 ? best : null;
  })();

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.post("/ai/ocr/extract", { doc_id: docId, doc_type: docType });
      setExtracted(r.data.extraction);
      setExpanded(true);
      toast.success("Fields extracted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Extraction failed");
    } finally {
      setLoading(false);
    }
  };

  const rows = FIELD_LABELS[docType] || [];
  const isDoc = extracted?.is_document !== false;
  const confidence = extracted?.confidence;

  return (
    <div className="mt-3 rounded-xl border border-blue-200 dark:border-blue-700/25 bg-blue-50/40 dark:bg-blue-700/5 p-4" data-testid={`ocr-block-${docId}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-700 to-orange-500 flex items-center justify-center shadow-md">
            <ScanText className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-blue-900 dark:text-blue-300">AI Document OCR</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">Extract every field from your {category} in seconds</div>
          </div>
        </div>
        <button
          onClick={run}
          disabled={loading}
          className="btn-signature rounded-lg px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
          data-testid={`ocr-run-${docId}`}
        >
          {loading ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analysing…</>
          ) : extracted ? (
            <><Sparkles className="w-3.5 h-3.5" /> Re-run OCR</>
          ) : (
            <><Sparkles className="w-3.5 h-3.5" /> Extract fields</>
          )}
        </button>
      </div>

      <AnimatePresence>
        {expanded && extracted && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            {!isDoc ? (
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 p-3 text-sm text-amber-800 dark:text-amber-300">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>This doesn&apos;t look like a {category}. Try re-uploading a clearer photo of the document.</div>
              </div>
            ) : (
              <>
                {typeof confidence === "number" && (
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                    <span className="text-slate-600 dark:text-slate-400">Confidence:</span>
                    <div className="flex-1 max-w-[200px] h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-700 to-emerald-500 rounded-full"
                        style={{ width: `${Math.round(confidence * 100)}%` }}
                      />
                    </div>
                    <span className="font-mono font-bold text-slate-700 dark:text-slate-300">{Math.round(confidence * 100)}%</span>
                  </div>
                )}
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                  {rows.map(([k, label]) => (
                    <div key={k} className="flex items-baseline justify-between border-b border-dashed border-slate-200 dark:border-slate-700/50 py-1.5" data-testid={`ocr-field-${k}`}>
                      <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 dark:text-slate-400">{label}</span>
                      <span className="text-sm font-semibold text-slate-900 dark:text-white truncate ml-2 max-w-[60%] text-right">{formatValue(k, extracted[k])}</span>
                    </div>
                  ))}
                </div>
                {extracted.warnings?.length > 0 && (
                  <div className="mt-3 text-[11px] text-amber-700 dark:text-amber-400">
                    ⚠️ {extracted.warnings.join(" · ")}
                  </div>
                )}
                {suggestedInsurer && (
                  <Link
                    to={`/insurer/${suggestedInsurer.slug}`}
                    className="mt-4 flex items-center gap-3 rounded-xl bg-gradient-to-r from-[#003DA5]/10 to-[#FF7A00]/10 dark:from-[#003DA5]/20 dark:to-[#FF7A00]/15 border border-[#003DA5]/30 dark:border-[#003DA5]/40 p-3 hover:from-[#003DA5]/20 hover:to-[#FF7A00]/20 transition-all group"
                    data-testid={`ocr-insurer-suggest-${suggestedInsurer.slug}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-white dark:bg-slate-900 shadow flex items-center justify-center shrink-0">
                      <Sparkles className="w-4 h-4 text-[#FF7A00]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">Auto-detected insurer</div>
                      <div className="text-sm font-bold text-slate-900 dark:text-white truncate">{suggestedInsurer.name} · claim guide</div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-500 group-hover:translate-x-0.5 transition-transform shrink-0" />
                  </Link>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
